import { faGhost } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Tooltip } from '@pickleballinc/react-ui';
import { trim } from 'lodash';
import { useEffect, useState } from 'react';

import { useSessionAuth } from '@/contexts/sessionAuth/SessionAuthProvider';
import { PlayersOnStandings, SessionLeagueDetails } from '@/modules/league/new-components/types';
import { getConnectDUPRURL } from '@/utils/helpers/getConnectDUPRURL';

interface PlayerInfoProps {
  firstName: string;
  lastName: string;
  userId?: string;
  isDuprActive: boolean;
  countGamesTowardRatings: boolean;
  imageUrl: string;
}

const PlayerInfo = ({
  firstName,
  lastName,
  userId,
  isDuprActive,
  countGamesTowardRatings,
  imageUrl,
}: PlayerInfoProps) => {
  if (!firstName && !lastName) return null;
  const { user } = useSessionAuth();
  const [baseURL, setBaseURL] = useState('');
  const isCurrentUser =
    user?.session.pbUuid && userId?.toLowerCase() === user?.session.pbUuid.toLowerCase();

  useEffect(() => {
    setBaseURL(window.location.href);
  }, []);

  return (
    <div className='flex gap-1 items-center'>
      <Avatar size='sm' imageUrl={imageUrl} />
      {`${firstName} ${lastName}`}
      {countGamesTowardRatings && !isDuprActive ? (
        <span className='text-xs font-medium text-gray-600'>
          {isCurrentUser ? (
            <a
              className='text-primary-500 underline'
              href={getConnectDUPRURL(userId, 'l', baseURL, baseURL)}
              aria-label='Link to connect to DUPR'
            >
              Connect To DUPR
            </a>
          ) : (
            '(Not Linked to DUPR)'
          )}
        </span>
      ) : null}
    </div>
  );
};

interface PlayerNameRowProps {
  session: SessionLeagueDetails;
  player: PlayersOnStandings;
}

export default function PlayerNameRow({ session, player }: PlayerNameRowProps) {
  return (
    <div className='w-80 shrink-0 flex-col break-words border-r px-2 py-2 text-sm font-semibold gap-2 flex justify-center'>
      <PlayerInfo
        firstName={player.FirstName_1}
        lastName={player.LastName_1}
        userId={player.UserID_1}
        isDuprActive={player.DUPR1Active}
        countGamesTowardRatings={session.CountGamesTowardRatings}
        imageUrl={player.ProfileImage_1}
      />

      {player.FirstName_2 !== '' && player.LastName_2 !== '' ? (
        <div className='flex flex-row items-center'>
          <PlayerInfo
            firstName={player.FirstName_2}
            lastName={player.LastName_2}
            userId={player.UserID_2}
            isDuprActive={player.DUPR2Active}
            countGamesTowardRatings={session.CountGamesTowardRatings}
            imageUrl={player.ProfileImage_2}
          />
          {trim(player.AttendeeSessionID_2).length === 0 ? (
            <Tooltip placement='top-start'>
              <Tooltip.Trigger asChild>
                <FontAwesomeIcon icon={faGhost} className='ml-2 block' />
              </Tooltip.Trigger>
              <Tooltip.Content className='w-42 rounded bg-black bg-black/75 p-2 text-sm text-white'>
                Not Registered
              </Tooltip.Content>
            </Tooltip>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
